import * as React from 'react'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { Tooltip, Button as MuiButton } from '@mui/material'
import ThemeConfig from '../../theme/ThemeConfig'

const PREFIX = 'Button'

const classes = {
  root: `${PREFIX}-root`,
  contained: `${PREFIX}-contained`,
  outlined: `${PREFIX}-outlined`,
  text: `${PREFIX}-text`,
  bulkActions: `${PREFIX}-bulkActions`,
  buttonDisabled: `${PREFIX}-buttonDisabled`,
}

const StyledMuiButton = styled(MuiButton, {
  shouldForwardProp: prop => prop !== 'color',
})(color => {
  return {
    [`&.${classes.root}`]: {
      margin: ThemeConfig.spacing(0.5),
    },
    [`&.${classes.contained}`]: {
      color: ThemeConfig.palette[color.color].contrastText,
      backgroundColor: ThemeConfig.palette[color.color].main,
      '&:hover': {
        backgroundColor: ThemeConfig.palette[color.color].dark,
      },
    },
    [`&.${classes.outlined}`]: {
      color: ThemeConfig.palette[color.color].main,
      borderColor: ThemeConfig.palette[color.color].main,
      '&:hover': {
        backgroundColor: ThemeConfig.palette[color.color].light,
      },
    },
    [`&.${classes.text}`]: {
      color: ThemeConfig.palette[color.color].dark,
      '&:hover': {
        backgroundColor: ThemeConfig.palette[color.color].light,
      },
      '&$buttonDisabled': {
        color: ThemeConfig.palette.background.paper,
        opacity: '0.6',
      },
    },
    [`&.${classes.bulkActions}`]: {
      color: ThemeConfig.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: ThemeConfig.palette.primary.dark,
      },
      '&$buttonDisabled': {
        color: ThemeConfig.palette.background.paper,
        opacity: '0.6',
      },
    },
    [`&.${classes.buttonDisabled}`]: {
      color: '#fff',
      backgroundColor: '#000000',
      opacity: 0.1,
    },
  }
})

const styledIcon = icon => {
  const iconStyle = {
    marginRight: ThemeConfig.spacing(0.5),
    fontSize: 26,
  }
  return React.cloneElement(icon, {
    style: iconStyle,
  })
}

export const Button = props => {
  const {
    color,
    variant,
    size,
    onClick,
    disabled,
    bulkActions,
    toolTip,
    ...other
  } = props
  let defaultVariant
  let verifiedColor = color
  const defaultSize = 'medium'
  //smart default for variant based on color as per APEX UX specification
  switch (verifiedColor) {
    case 'primary':
      defaultVariant = 'contained'
      break
    case 'secondary':
      defaultVariant = 'contained'
      break
    case 'tertiary':
      verifiedColor = 'primary'
      defaultVariant = 'text'
      break
    case 'error':
      defaultVariant = 'contained'
      break
    case 'warning':
      defaultVariant = 'outlined'
      break
    default:
      defaultVariant = 'contained'
      break
  }

  const fn = child => {
    if (child.type && child.type.muiName === 'SvgIcon') {
      return styledIcon(child)
    } else {
      return child
    }
  }
  const children =
    React.Children.count(props.children) > 1
      ? React.Children.map(props.children, fn)
      : props.children

  if (bulkActions === true) {
    return (
      <Tooltip title={toolTip || ''}>
        <span>
          <StyledMuiButton
            size={size ? size : defaultSize}
            disabled={!!disabled}
            onClick={onClick}
            color={verifiedColor}
            {...other}
            classes={{
              text: classes.bulkActions,
              disabled: classes.buttonDisabled,
            }}
          >
            {children}
          </StyledMuiButton>
        </span>
      </Tooltip>
    )
  } else {
    return (
      <StyledMuiButton
        variant={variant ? variant : defaultVariant}
        size={size ? size : defaultSize}
        classes={{
          root: classes.root,
          contained: classes.contained,
          outlined: classes.outlined,
          text: classes.text,
          disabled: classes.buttonDisabled,
        }}
        disabled={!!disabled}
        onClick={onClick}
        color={verifiedColor}
        {...other}
      >
        {children}
      </StyledMuiButton>
    )
  }
}

Button.propTypes = {
  color: PropTypes.string.isRequired,
  variant: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  bulkActions: PropTypes.bool,
  longActions: PropTypes.bool,
}

Button.muiName = MuiButton.muiName

export default Button
